/**
 * Cookie consent storage utilities
 * Handles GDPR-compliant cookie preferences
 */

const STORAGE_KEY = 'cookie-consent';
const CONSENT_VERSION = 1;
const CONSENT_EXPIRY_MONTHS = 12;

export interface CookiePreferences {
  analytics: boolean;
  timestamp: number;
  version: number;
}

/**
 * Get stored cookie consent preferences
 */
export function getConsentPreferences(): CookiePreferences | null {
  if (typeof window === 'undefined') return null;
  
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;
    
    const preferences: CookiePreferences = JSON.parse(stored);
    
    // Check if consent version is outdated
    if (preferences.version !== CONSENT_VERSION) {
      return null;
    }
    
    // Check if consent has expired (12 months)
    const expiryDate = new Date(preferences.timestamp);
    expiryDate.setMonth(expiryDate.getMonth() + CONSENT_EXPIRY_MONTHS);
    
    if (new Date() > expiryDate) {
      clearConsentPreferences();
      return null;
    }
    
    return preferences;
  } catch {
    return null;
  }
}

/**
 * Save cookie consent preferences
 */
export function saveConsentPreferences(analytics: boolean): void {
  if (typeof window === 'undefined') return;
  
  const preferences: CookiePreferences = {
    analytics,
    timestamp: Date.now(),
    version: CONSENT_VERSION,
  };
  
  localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
}

/**
 * Clear stored consent preferences
 */
export function clearConsentPreferences(): void {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(STORAGE_KEY);
}

/**
 * Check if user has made a consent choice
 */
export function hasConsentChoice(): boolean {
  return getConsentPreferences() !== null;
}

/**
 * Check if analytics is enabled
 */
export function isAnalyticsEnabled(): boolean {
  const preferences = getConsentPreferences();
  return preferences?.analytics ?? false;
}
